import { AssignmentRounded } from "@mui/icons-material";
import { Box, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import React from "react";
import dayjs from "dayjs";
import { Merchant } from "../../model/merchant_model";
import {
  TableCell,
  TableEmptyState,
  TableFooterPagination,
  TableHeaderRow,
  TableRow,
} from "../../../users/components/table_toolkit/table_toolkit";
import { black, greyEnabled, purple, purpleLight } from "../../../../theme/colors";

interface MerchantsTableInterface {
  data: Merchant[];
  count: number;
  page: number;
  setPage: (val: number) => void;
  pageSize: number;
  setPageSize: (val: number) => void;
  onAssignVouchers: (merchant: Merchant) => void;
}

const ROWS_PER_PAGE_OPTIONS = [8, 25, 50, 100];

function statusColors(status: string | null) {
  switch (status?.toLowerCase()) {
    case "approved":
      return { bgcolor: "#E7F5EC", color: "#1E6446" };
    case "pending":
      return { bgcolor: "#FAEDEA", color: "#D14C2E" };
    case "rejected":
      return { bgcolor: "#FBD9D6", color: "#C0392B" };
    default:
      return { bgcolor: "#F1E9F1", color: greyEnabled };
  }
}

export default function MerchantsTable({
  data,
  page,
  setPage,
  pageSize,
  setPageSize,
  count,
  onAssignVouchers,
}: MerchantsTableInterface) {
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const startRange = page * pageSize + 1;
  const endRange = Math.min((page + 1) * pageSize, count);
  const customText = `Showing ${count ? startRange : 0} to ${endRange || 0} of ${count || 0} entries`;
  const showList = data.length > 0;

  return (
    <Box px={{ xs: 2.5, sm: 3 }} pb={2}>
      {!showList ? (
        <TableEmptyState message="No merchants found" hint="Try adjusting your search or filters." />
      ) : (
        <Box mt={1}>
          <TableHeaderRow
            labels={[
              { label: "Sl. No.", flex: 0.3 },
              { label: "Merchant Id", flex: 0.7 },
              { label: "Merchant", flex: 1.6 },
              { label: "Status", flex: 0.6 },
              { label: "Pincode", flex: 0.6 },
              { label: "Location", flex: 1 },
              { label: "Created", flex: 0.9 },
              { label: "Actions" },
            ]}
            lastColumnWidth={90}
            align="left"
            bordered
          />
          {data.map((merchant, index) => (
            <TableRow key={merchant.id ?? index} bordered>
              <TableCell label="Sl. No." flex={0.3} bordered>
                <Typography fontSize={14} color={black} noWrap>
                  {page * pageSize + index + 1}
                </Typography>
              </TableCell>
              <TableCell label="Merchant Id" flex={0.7} bordered>
                <Tooltip title={merchant.id || ""}>
                  <Typography fontSize={14} color={black} noWrap sx={{ cursor: "help" }}>
                    {merchant.id || "-"}
                  </Typography>
                </Tooltip>
              </TableCell>
              <TableCell label="Merchant" flex={1.6} bordered>
                <Typography fontWeight={600} color={black} noWrap>
                  {merchant.name || "-"}
                </Typography>
                <Typography variant="body2" color={greyEnabled} noWrap>
                  {merchant.mobile_no || "-"}
                </Typography>
              </TableCell>
              <TableCell label="Status" flex={0.6} bordered>
                <Chip
                  label={merchant.status || "N/A"}
                  size="small"
                  sx={{ ...statusColors(merchant.status), fontWeight: 600, height: 20 }}
                />
              </TableCell>
              <TableCell label="Pincode" flex={0.6} bordered>
                <Typography fontSize={14} color={black} noWrap>
                  {merchant.pincode || "-"}
                </Typography>
              </TableCell>
              <TableCell label="Location" flex={1} bordered>
                <Typography fontSize={14} color={black} noWrap>
                  {merchant.location || "-"}
                </Typography>
              </TableCell>
              <TableCell label="Created" flex={0.9} bordered>
                <Typography fontSize={14} color={black} noWrap>
                  {merchant.createdAt ? dayjs(merchant.createdAt).format("DD-MM-YYYY") : "-"}
                </Typography>
                <Typography variant="body2" color={greyEnabled} noWrap>
                  {merchant.createdAt ? dayjs(merchant.createdAt).format("hh:mm A") : ""}
                </Typography>
              </TableCell>
              <TableCell isLast lastColumnWidth={90} bordered>
                <Box display="flex" justifyContent={{ xs: "flex-start", sm: "flex-end" }}>
                  <Tooltip title="Assign Vouchers">
                    <IconButton
                      onClick={() => onAssignVouchers(merchant)}
                      sx={{ bgcolor: purpleLight, "&:hover": { bgcolor: "#F0D9F0" } }}
                    >
                      <AssignmentRounded sx={{ color: purple, fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </Box>
      )}

      {showList && (
        <Box mt={2}>
          <TableFooterPagination
            count={count}
            page={page}
            onPageChange={handleChangePage}
            summaryText={customText}
            rowsPerPage={pageSize}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            onRowsPerPageChange={setPageSize}
          />
        </Box>
      )}
    </Box>
  );
}
